/**
 * The current acquisition's date, in the viewer's playback bar, opening a
 * calendar to jump to another day. Only days with an acquisition can be
 * picked; picking one lands on its first acquisition, and the arrows step
 * through the rest of that day from there.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon } from 'lucide-react';
import { enUS, fr } from 'react-day-picker/locale';
import { Calendar } from '@/components/ui/calendar';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

// A local calendar day, the way the user reads the dates.
const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

interface AcquisitionDatePickerProps {
    images: { acquisitionDate: string | Date }[];
    currentIndex: number;
    onSelect: (index: number) => void;
}

export default function AcquisitionDatePicker({
    images,
    currentIndex,
    onSelect,
}: AcquisitionDatePickerProps) {
    const { t, i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const lang = i18n.language;

    // Each day that has acquisitions -> the index of its first one. The list
    // is in date order, so the first seen is the earliest.
    const firstIndexByDay = useMemo(() => {
        const map = new Map<string, number>();
        images.forEach((img, i) => {
            const key = dayKey(new Date(img.acquisitionDate));
            if (!map.has(key)) map.set(key, i);
        });
        return map;
    }, [images]);

    const current = new Date(images[currentIndex].acquisitionDate);
    const first = new Date(images[0].acquisitionDate);
    const last = new Date(images[images.length - 1].acquisitionDate);

    const label = `${current.toLocaleDateString(lang, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    })} · ${current.toLocaleTimeString(lang, {
        hour: '2-digit',
        minute: '2-digit',
    })}`;

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger
                aria-label={t('Choose a date')}
                title={t('Choose a date')}
                className={cn(
                    'inline-flex h-8 items-center gap-1.5 rounded-full pl-3 pr-2.5 text-xs font-medium tabular-nums text-white/85 transition-colors',
                    'hover:bg-white/15 hover:text-white',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60',
                    open && 'bg-white/15 text-white',
                )}
            >
                {label}
                <ChevronDownIcon
                    className={cn(
                        'h-3.5 w-3.5 text-white/60 transition-transform',
                        open && 'rotate-180',
                    )}
                />
            </PopoverTrigger>
            <PopoverContent
                side="top"
                sideOffset={10}
                // Keep focus off the trigger once closed: space and the arrow
                // keys drive playback, and a focused trigger would reopen.
                onCloseAutoFocus={(e) => e.preventDefault()}
            >
                <Calendar
                    mode="single"
                    required
                    // Month and year lists, bounded to the experiment's span:
                    // some run for years.
                    captionLayout="dropdown"
                    locale={lang.startsWith('fr') ? fr : enUS}
                    selected={current}
                    defaultMonth={current}
                    startMonth={first}
                    endMonth={last}
                    disabled={(date) => !firstIndexByDay.has(dayKey(date))}
                    onSelect={(date) => {
                        const index = firstIndexByDay.get(dayKey(date));
                        if (index === undefined) return;
                        onSelect(index);
                        setOpen(false);
                    }}
                />
            </PopoverContent>
        </Popover>
    );
}
